import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, TextInput, Toggle } from '@opendate-app/setlist';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import AddWindowPicker from './AddWindowPicker';
import PresaleWindowEditor from './PresaleWindowEditor';
import {
  DATE_AND_TIME, PRESALE_PRESETS, SHOW_DETAIL_BASED, buildWindow, formatDateTime, heldCount, isCustomPrice, isEditedFromOffer,
  liveWindows, removeWindow, revertToOffer, sortWindows, toInt, toPrice, upsertWindow, windowEndLabel, windowEndTime, windowKey,
  windowStartLabel, windowStartTime, windowSubtitle,
} from '../utils/presaleWindows';
import { ticketTypeToPhases, phasesToTicketType, dayOfBoundaryFor, DAY_OF_NAME } from '../utils/ticketPhases';
import { quantityPricePoint } from '../utils/ticketPricePoints';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';

import './SalesWindows.css';

const priceMask = createNumberMask({ prefix: '', allowDecimal: true, decimalLimit: 2, integerLimit: null });

export const deriveTag = (price) => (price == null ? 'SAME AS TICKET' : 'CUSTOM · REVERT');

const findShowActivityByName = (showActivities, name) =>
  showActivities.find((detail) => detail.name === name);

const DOORS_NAME = 'Doors';
const EVENT_END_NAME = 'Event End';
const EVENT_START_NAME = 'Event Start';

const toBrowserTime = (value) => (typeof value === 'string' ? offsetDateForBrowser(value) : value);

export const deriveDayOfStartsAt = (showActivities, confirm) => {
  const atFor = (name) => {
    const activity = findShowActivityByName(showActivities, name);
    return activity && activity.at ? activity.at : null;
  };
  const eventAt = (confirm && confirm.start_time)
    || atFor(EVENT_START_NAME)
    || atFor(DOORS_NAME)
    || atFor(DAY_OF_NAME)
    || atFor(EVENT_END_NAME)
    || (confirm && confirm.end_time);
  if (!eventAt) return null;

  const midnight = new Date(toBrowserTime(eventAt));
  midnight.setHours(0, 0, 0, 0);
  return midnight;
};

export const endSalesAtEventEnd = (ticketType, showActivities) => {
  const eventEnd = findShowActivityByName(showActivities, EVENT_END_NAME);
  if (!eventEnd) return ticketType;
  if (ticketType.availability === DATE_AND_TIME) return { ...ticketType, end_time: offsetDateForBrowser(eventEnd.at) };
  return { ...ticketType, end_show_activity_id: eventEnd.id };
};

export const dayOfTogglePhases = (phases, enabled, showActivities, confirm, publicPrice) => {
  const publicAmount = parseFloat(String(publicPrice || '').replace(/,/g, ''));
  const price = enabled && phases.dayOf.price == null && publicAmount > 0
    ? (publicAmount + 5).toFixed(2)
    : phases.dayOf.price;
  const startsAt = enabled && !phases.dayOf.startsAt
    ? deriveDayOfStartsAt(showActivities, confirm)
    : phases.dayOf.startsAt;

  return {
    ...phases,
    dayOf: {
      ...phases.dayOf,
      enabled,
      price,
      newRecord: enabled && !phases.dayOf.id ? true : phases.dayOf.newRecord,
      startsAt,
      name: (phases.dayOf.id && !phases.dayOf.newRecord) ? phases.dayOf.name : (phases.dayOf.name || DAY_OF_NAME),
    },
  };
};

const PUBLIC_KEY = 'public-on-sale';
const DAY_OF_KEY = 'day-of-show';

const ONE_MINUTE = 60 * 1000;
const sameTime = (a, b) => (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
const isCustom = (w) => w.availability === DATE_AND_TIME;
const asCustom = (ticketType, startTime, endTime) => ({
  ...ticketType,
  availability: DATE_AND_TIME,
  start_show_activity_id: null,
  end_show_activity_id: null,
  start_time: startTime,
  end_time: endTime,
});

export const publicEndTime = (ticketType, phases, showActivities) => (phases.dayOf.enabled && phases.dayOf.startsAt
  ? new Date(new Date(toBrowserTime(phases.dayOf.startsAt)).getTime() - ONE_MINUTE)
  : windowEndTime(ticketType, showActivities));

export const publicDraft = (ticketType, phases, showActivities) => {
  const base = { key: PUBLIC_KEY, name: 'Public on-sale' };
  if (!phases.dayOf.enabled) {
    return {
      ...base,
      availability: isCustom(ticketType) ? DATE_AND_TIME : SHOW_DETAIL_BASED,
      start_show_activity_id: ticketType.start_show_activity_id,
      end_show_activity_id: ticketType.end_show_activity_id,
      start_time: ticketType.start_time,
      end_time: ticketType.end_time,
    };
  }
  return {
    ...base, availability: DATE_AND_TIME, start_show_activity_id: null, end_show_activity_id: null,
    start_time: windowStartTime(ticketType, showActivities), end_time: publicEndTime(ticketType, phases, showActivities),
  };
};

const withStart = (ticketType, draft, showActivities) => {
  if (sameTime(windowStartTime(draft, showActivities), windowStartTime(ticketType, showActivities))) return ticketType;
  if (!isCustom(draft) && !isCustom(ticketType)) return { ...ticketType, start_show_activity_id: draft.start_show_activity_id };
  return asCustom(ticketType, windowStartTime(draft, showActivities), windowEndTime(ticketType, showActivities));
};

export const applyPublicDraft = (ticketType, phases, draft, showActivities) => {
  if (!phases.dayOf.enabled) {
    return {
      ...ticketType,
      availability: draft.availability,
      start_show_activity_id: draft.start_show_activity_id,
      end_show_activity_id: draft.end_show_activity_id,
      start_time: draft.start_time,
      end_time: draft.end_time,
    };
  }
  const startsAt = new Date(new Date(windowEndTime(draft, showActivities)).getTime() + ONE_MINUTE);
  const next = phasesToTicketType(ticketType, { ...phases, dayOf: { ...phases.dayOf, startsAt } });
  return withStart(next, draft, showActivities);
};

const activityAt = (showActivities, startsAt) => showActivities.find(
  (a) => startsAt && new Date(a.at).getTime() === new Date(startsAt).getTime()
);

export const dayOfDraft = (ticketType, startsAt, showActivities) => {
  const start = activityAt(showActivities, startsAt);
  const base = { key: DAY_OF_KEY, name: DAY_OF_NAME };
  if (start && !isCustom(ticketType)) {
    return {
      ...base, availability: SHOW_DETAIL_BASED, start_time: null, end_time: null,
      start_show_activity_id: start.id, end_show_activity_id: ticketType.end_show_activity_id,
    };
  }
  return {
    ...base, availability: DATE_AND_TIME, start_show_activity_id: null, end_show_activity_id: null,
    start_time: toBrowserTime(startsAt), end_time: windowEndTime(ticketType, showActivities),
  };
};

export const applyDayOfDraft = (ticketType, phases, draft, showActivities) => {
  const startActivity = showActivities.find((a) => a.id === draft.start_show_activity_id);
  const startsAt = isCustom(draft) ? draft.start_time : startActivity.at;
  const next = phasesToTicketType(ticketType, { ...phases, dayOf: { ...phases.dayOf, startsAt } });
  const endTime = windowEndTime(draft, showActivities);
  if (sameTime(endTime, windowEndTime(ticketType, showActivities))) return next;
  if (!isCustom(draft) && !isCustom(ticketType)) return { ...next, end_show_activity_id: draft.end_show_activity_id };
  return asCustom(next, windowStartTime(ticketType, showActivities), endTime);
};

const InheritanceTag = ({ price, onRevert, disabled }) => {
  const tag = deriveTag(price);
  if (price == null) {
    return <Badge color="gray" size="sm">{tag}</Badge>;
  }

  return (
    <button type="button" className="sales-window-revert-tag" disabled={disabled} onClick={onRevert}>
      <Badge color="warning" size="sm">{tag}</Badge>
    </button>
  );
};

InheritanceTag.propTypes = {
  price: PropTypes.string,
  onRevert: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

InheritanceTag.defaultProps = {
  price: null,
  disabled: false,
};

const PhasePrice = ({ value, disabled, showPrefix, masked, onChange }) => (
  <div className="sales-window-price-field">
    <TextInput
      type="text"
      size="sm"
      leadingText={showPrefix ? '$' : undefined}
      value={value || ''}
      disabled={disabled}
      onChange={onChange}
      {...(masked ? { as: MaskedInput, mask: priceMask } : {})}
    />
  </div>
);

PhasePrice.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool,
  showPrefix: PropTypes.bool,
  masked: PropTypes.bool,
  onChange: PropTypes.func,
};

PhasePrice.defaultProps = {
  value: '',
  disabled: false,
  showPrefix: true,
  masked: false,
  onChange: undefined,
};

const PresaleWindowRow = ({ window: w, ticketType, showActivities, editable, onChange, onEdit }) => {
  const isFree = Boolean(ticketType.free);
  const custom = isCustomPrice(w, ticketType);
  const enabled = w.enabled !== false && !w.ended;
  const edited = isEditedFromOffer(w, ticketType);
  const basePrice = isFree ? 'Free' : ticketType.base_price_two_decimals;

  const toggle = (event) => {
    if (event.target.checked && w.id && !enabled) {
      onEdit({ ...w, enabled: true, ended: false, reenabling: true });
      return;
    }
    onChange({ ...w, enabled: event.target.checked });
  };

  return (
    <div className={`sales-window-row sales-window-row--presale ${enabled ? '' : 'sales-window-row--disabled'}`}>
      <div className="sales-window-lead">
        <Toggle checked={enabled} disabled={!editable} onChange={toggle} aria-label={`Sell ${w.name}`} />
      </div>
      <div className="sales-window-info">
        <span className="sales-window-title">
          <span className="sales-window-name">{w.name}</span>
          {w.offer_snapshot && !edited ? <Badge color="brand" size="sm">FROM OFFER</Badge> : null}
          {edited ? (
            <button type="button" className="sales-window-revert-tag" disabled={!editable} onClick={() => onChange(revertToOffer(w))}>
              <Badge color="warning" size="sm">EDITED · REVERT TO OFFER</Badge>
            </button>
          ) : null}
        </span>
        <span className="sales-window-sub">
          {windowSubtitle(w, ticketType, showActivities)}
          {toInt(w.sold_count) > 0 ? ` · ${toInt(w.sold_count)} sold` : ''}
          {w.access_code ? ` · code ${w.access_code}` : ''}
        </span>
        {editable ? (
          <span>
            <Button variant="link-color" size="sm" className="sales-window-edit" aria-label={`Edit ${w.name}`} onClick={() => onEdit(w)}>
              Edit window
            </Button>
          </span>
        ) : null}
      </div>
      {isFree ? null : (
        <div className={`sales-window-price ${enabled ? '' : 'sales-window-price--muted'}`}>
          <PhasePrice
            value={custom ? w.price_two_decimals : basePrice}
            disabled={!enabled || !editable}
            masked
            onChange={(event) => {
              const { value } = event.target;
              onChange({ ...w, price_two_decimals: value === '' ? null : value });
            }}
          />
          <InheritanceTag price={enabled && custom ? w.price_two_decimals : null} disabled={!editable}
            onRevert={() => onChange({ ...w, price_two_decimals: null })} />
        </div>
      )}
    </div>
  );
};

PresaleWindowRow.propTypes = {
  window: PropTypes.shape({ name: PropTypes.string }).isRequired,
  ticketType: PropTypes.shape({ free: PropTypes.bool }).isRequired,
  showActivities: PropTypes.arrayOf(PropTypes.object).isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const SalesWindows = ({ ticketType, showActivities, confirm, onChange, onEditingChange }) => {
  const [editing, setEditing] = useState(null);
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  useEffect(() => { if (onEditingChangeRef.current) onEditingChangeRef.current(Boolean(editing)); }, [editing]);
  useEffect(() => () => { if (onEditingChangeRef.current) onEditingChangeRef.current(false); }, []);
  const windows = sortWindows(liveWindows(ticketType), showActivities);
  const phases = ticketTypeToPhases(ticketType, dayOfBoundaryFor(confirm));
  const saveWindow = (w) => {
    if (w.key === PUBLIC_KEY) onChange(applyPublicDraft(ticketType, phases, w, showActivities));
    else if (w.key === DAY_OF_KEY) onChange(applyDayOfDraft(ticketType, phases, w, showActivities));
    else onChange(upsertWindow(ticketType, w));
    setEditing(null);
  };
  const dropWindow = (w) => { onChange(removeWindow(ticketType, w)); setEditing(null); };
  const editingKey = editing ? windowKey(editing) : null;
  const isFree = Boolean(ticketType.free);
  const basePriceDisplay = isFree ? 'Free' : ticketType.base_price_two_decimals;

  const emitPhases = (nextPhases) => {
    onChange(phasesToTicketType(ticketType, nextPhases));
  };

  const handleDayOfToggle = (event) => {
    const { checked } = event.target;
    const next = phasesToTicketType(
      ticketType,
      dayOfTogglePhases(phases, checked, showActivities, confirm, ticketType.base_price_two_decimals)
    );
    onChange(checked && !phases.dayOf.startsAt ? endSalesAtEventEnd(next, showActivities) : next);
  };

  const handleDayOfPriceChange = (event) => {
    const { value } = event.target;
    emitPhases({
      ...phases,
      dayOf: { ...phases.dayOf, price: value === '' ? null : value },
    });
  };

  const handleDayOfRevert = () => {
    emitPhases({ ...phases, dayOf: { ...phases.dayOf, enabled: false } });
  };

  const dayOfPriceValue = phases.dayOf.price == null ? basePriceDisplay : phases.dayOf.price;

  const dayOfActivity = activityAt(showActivities, phases.dayOf.startsAt);
  const dayOfStart = dayOfActivity ? dayOfActivity.name : formatDateTime(toBrowserTime(phases.dayOf.startsAt));
  const dayOfSub = `${dayOfStart || 'Doors'} → ${windowEndLabel(ticketType, showActivities) || 'Event end'}`;
  const publicStart = windowStartLabel(ticketType, showActivities);
  const publicEnd = phases.dayOf.enabled ? formatDateTime(publicEndTime(ticketType, phases, showActivities)) : windowEndLabel(ticketType, showActivities);
  const publicRange = publicStart && publicEnd ? `${publicStart} → ${publicEnd}` : null;

  const pool = toInt(ticketType.available);
  const held = heldCount(ticketType);
  const overPool = pool > 0 && held > pool;
  const basePrice = ticketType.base_price_two_decimals || '0.00';
  const presaleText = windows.filter((w) => w.enabled !== false && !w.ended)
    .map((w) => `$${isCustomPrice(w, ticketType) ? toPrice(w.price_two_decimals).toFixed(2) : basePrice} ${w.name.toLowerCase()}`);
  let summaryLine;
  if (overPool) {
    summaryLine = `Pre-sale windows hold ${held} tickets but only ${pool} are available. Lower a window's quantity or raise available quantity.`;
  } else if (isFree) {
    summaryLine = `Free ticket — ${ticketType.available || '0'} available at public on-sale.`;
  } else {
    summaryLine = `Sells at ${presaleText.length ? `${presaleText.join(', ')}, then ` : ''}$${basePrice} on public on-sale${
      phases.dayOf.enabled ? `, $${dayOfPriceValue || '0.00'} day of show` : ''
    }. ${ticketType.available || '0'} tickets total${held ? `, ${held} held for pre-sale` : ''}.`;
  }

  const startFirstWindow = () => {
    const preset = PRESALE_PRESETS.find((candidate) => showActivities.some((a) => a.name === candidate.startName));
    setEditing(buildWindow(preset || null, ticketType, showActivities));
  };

  return (
    <div className="sales-windows-group">
      <div className="sales-windows">
        {windows.length === 0 && !editing ? (
          <div className="sales-window-row sales-window-row--disabled sales-window-row--empty-presale">
            <div className="sales-window-lead">
              <Toggle checked={false} aria-label="Add a pre-sale window" onChange={(event) => event.target.checked && startFirstWindow()} />
            </div>
            <div className="sales-window-info">
              <span className="sales-window-title">Pre-sale</span>
              <span className="sales-window-sub">Off — ticket goes on sale to everyone at public on-sale</span>
            </div>
          </div>
        ) : null}

        {windows.map((w) => (editing && windowKey(editing) === windowKey(w) ? (
          <PresaleWindowEditor key={windowKey(w)} window={editing} ticketType={ticketType}
            showActivities={showActivities} onSave={saveWindow} onCancel={() => setEditing(null)} onRemove={dropWindow} />
        ) : (
          <PresaleWindowRow key={windowKey(w)} window={w} ticketType={ticketType} showActivities={showActivities}
            editable={!editing} onChange={(next) => onChange(upsertWindow(ticketType, next))} onEdit={setEditing} />
        )))}

        {editing && editing.newRecord && !windows.some((w) => windowKey(w) === windowKey(editing)) ? (
          <PresaleWindowEditor window={editing} ticketType={ticketType} showActivities={showActivities} isNew
            onSave={saveWindow} onCancel={() => setEditing(null)} onRemove={dropWindow} />
        ) : null}

        {phases.presale.pricePoints.some((pricePoint) => !quantityPricePoint(pricePoint)) ? (
          <ul className="sales-window-presale-list">
            {phases.presale.pricePoints.filter((pricePoint) => !quantityPricePoint(pricePoint)).map((pricePoint) => (
              <li key={pricePoint.id || pricePoint.name} className="sales-window-presale-item">
                <span>{pricePoint.name} (scheduled price)</span>
                <span>{pricePoint.price_two_decimals}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {editingKey === PUBLIC_KEY ? (
          <PresaleWindowEditor window={editing} ticketType={ticketType} showActivities={showActivities} variant="public"
            endBefore={phases.dayOf.enabled ? windowEndTime(ticketType, showActivities) : null}
            onSave={saveWindow} onCancel={() => setEditing(null)} onRemove={dropWindow} />
        ) : (
          <div className="sales-window-row sales-window-row--public">
            <span className="sales-window-always">ALWAYS</span>
            <div className="sales-window-info">
              <span className="sales-window-title">Public on-sale</span>
              <span className="sales-window-sub">{publicRange ? `${publicRange} · everyone` : 'Everyone'}</span>
              {editing ? null : (
                <span>
                  <Button variant="link-color" size="sm" className="sales-window-edit" aria-label="Edit Public on-sale"
                    onClick={() => setEditing(publicDraft(ticketType, phases, showActivities))}>
                    Edit window
                  </Button>
                </span>
              )}
            </div>
            <div className="sales-window-price">
              <PhasePrice value={basePriceDisplay} disabled showPrefix={!isFree} />
              <Badge color="gray" size="sm">{deriveTag(null)}</Badge>
            </div>
          </div>
        )}

        {!isFree && editingKey === DAY_OF_KEY ? (
          <PresaleWindowEditor window={editing} ticketType={ticketType} showActivities={showActivities} variant="dayOf"
            startAfter={windowStartTime(ticketType, showActivities)}
            onSave={saveWindow} onCancel={() => setEditing(null)} onRemove={dropWindow} />
        ) : null}

        {!isFree && editingKey !== DAY_OF_KEY ? (
          <div className={`sales-window-row sales-window-row--day-of ${phases.dayOf.enabled ? '' : 'sales-window-row--disabled'}`}>
            <div className="sales-window-lead">
              <Toggle checked={phases.dayOf.enabled} disabled={Boolean(editing) || phases.presale.pricePoints.some(quantityPricePoint)} onChange={handleDayOfToggle} aria-label="Sell Day of show" />
            </div>
            <div className="sales-window-info">
              <span className="sales-window-title">Day of show</span>
              <span className="sales-window-sub">{dayOfSub}</span>
              {phases.dayOf.enabled && !editing ? (
                <span>
                  <Button variant="link-color" size="sm" className="sales-window-edit" aria-label="Edit Day of show"
                    onClick={() => setEditing(dayOfDraft(ticketType, phases.dayOf.startsAt, showActivities))}>
                    Edit window
                  </Button>
                </span>
              ) : null}
            </div>
            <div className={`sales-window-price ${phases.dayOf.enabled ? '' : 'sales-window-price--muted'}`}>
              <PhasePrice
                value={dayOfPriceValue}
                disabled={!phases.dayOf.enabled}
                showPrefix
                masked
                onChange={handleDayOfPriceChange}
              />
              <InheritanceTag
                price={phases.dayOf.enabled ? phases.dayOf.price : null}
                onRevert={handleDayOfRevert}
              />
            </div>
          </div>
        ) : null}

        <div className="sales-window-add">
          <AddWindowPicker showActivities={showActivities} disabled={Boolean(editing)}
            onPick={(preset) => setEditing(buildWindow(preset, ticketType, showActivities))} />
        </div>
      </div>

      <p className={`sales-windows-summary ${overPool ? 'sales-windows-summary--error' : ''}`}>{summaryLine}</p>
    </div>
  );
};

SalesWindows.propTypes = {
  ticketType: PropTypes.shape({
    free: PropTypes.bool,
    base_price_two_decimals: PropTypes.string,
    available: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  showActivities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      at: PropTypes.string.isRequired,
    })
  ).isRequired,
  confirm: PropTypes.shape({
    start_time: PropTypes.string,
    end_time: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onEditingChange: PropTypes.func,
};

SalesWindows.defaultProps = {
  confirm: null,
  onEditingChange: undefined,
};

export default SalesWindows;

